import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Download, FileText, Loader2, Pencil, Upload } from 'lucide-react';
import { Company } from '../types/company';
import { Deal } from '../types/deal';
import { Cashflow } from '../types/cashflow';
import { Vehicle } from '../types/vehicle';
import { companyApi } from '../services/companyApi';
import {
  CompanyDetail, FIN_PERIODS, FIN_PERIOD_LABEL, FIN_SOURCE, Financial, IP_KIND, IP_STATUS, NOTE_KIND, REQ_STATUS, RISK_STATUS, companyDetailApi,
  financials, govPrograms, history, ipRights, loans, notes, rounds, shareChanges, shareholders,
} from '../services/companyDetailApi';
import { ACCEPT, DOC_KIND, DOC_STATUS, Doc, documentApi, guessKind } from '../services/documentApi';
import { MGMT_LEVEL, RISK_GRADE, SHARE_KIND } from '../services/codes';
import { missingFinancial, missingProfile } from '../lib/vics';
import { todayLocal } from '../lib/date';
import { Col, EditTable, money } from './EditTable';

/**
 * 투자처 상세의 탭 — factsheet 「포트폴리오」 5탭 대조 (2026-10-08) + 우리 것 둘.
 *
 * 기업정보 · 투자정보 · 재무 · 정기보고 · 리스크평가 · 문서 보관함 · 타임라인.
 * 주주명부는 **전체 주주**를 적는다 — 우리 지분만으로는 희석을 계산할 수 없다.
 * 정기보고 · 리스크평가는 읽기만 — 쓰는 곳은 각 화면이다.
 * 타임라인의 투자 · 회수 줄은 원장에서 끌어온다(따로 적지 않는다).
 */
const TABS = ['기업정보', '투자정보', '재무', '정기보고', '리스크평가', '문서 보관함', '타임라인'] as const;
type Tab = (typeof TABS)[number];

const Vics: React.FC<{ menu: string }> = ({ menu }) => (
  <span className="ml-1.5 rounded px-1 py-0.5 text-[11px] font-semibold bg-sky-50 text-sky-700">VICS {menu}</span>
);
const H: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <h4 className="px-4 pt-4 pb-1.5 text-xs font-semibold text-slate-500">{children}</h4>
);
const Empty: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <p className="px-4 py-3 text-sm text-slate-400">{children}</p>
);

const REQ_STYLE: Record<string, string> = {
  not_sent: 'bg-slate-100 text-slate-500', requested: 'bg-amber-50 text-amber-700', submitted: 'bg-sky-50 text-sky-700',
  confirmed: 'bg-emerald-50 text-emerald-700', rejected: 'bg-red-50 text-red-600',
};
const GRADE_STYLE: Record<string, string> = {
  growth: 'bg-emerald-50 text-emerald-700', expected: 'bg-teal-50 text-teal-700', watch: 'bg-sky-50 text-sky-700',
  caution: 'bg-amber-50 text-amber-700', concern: 'bg-orange-50 text-orange-700', danger: 'bg-red-50 text-red-600',
};
const DOC_STATUS_STYLE: Record<string, string> = {
  pending: 'text-slate-500', extracted: 'text-amber-700', checked: 'text-emerald-700', skipped: 'text-slate-400',
};
const opts = (m: Record<string, string>) => Object.entries(m) as [string, string][];
const dot = (s: string | null | undefined) => (s ? s.slice(0, 10).replace(/-/g, '.') : '—');

/** 공통 REST 한 자원을 EditTable 의 저장 · 지우기로 */
function crud<T extends { id?: number }>(api: { create: (b: Partial<T>) => Promise<T>; update: (id: number, b: Partial<T>) => Promise<T>; remove: (id: number) => Promise<unknown> }, companyId: number) {
  return {
    onSave: (id: number | null, v: Partial<T>) => (id ? api.update(id, v) : api.create({ ...v, companyId } as Partial<T>)),
    onRemove: (id: number) => api.remove(id),
  };
}

interface Props {
  company: Company;
  deals: Deal[];
  flows: Cashflow[];
  vehicles: Vehicle[];
  onCompanyChange: () => void;
}

export const CompanyTabs: React.FC<Props> = ({ company, deals, flows, vehicles, onCompanyChange }) => {
  const companyId = company.id;
  // ?tab=재무 로 바로 열 수 있다 — 문서함 · 알림에서 건너올 때
  const [params] = useSearchParams();
  const [tab, setTab] = useState<Tab>(() => (TABS as readonly string[]).includes(params.get('tab') ?? '') ? (params.get('tab') as Tab) : '기업정보');
  const [d, setD] = useState<CompanyDetail | null>(null);
  const [docs, setDocs] = useState<Doc[]>([]);
  const dealIds = useMemo(() => deals.map((x) => x.id), [deals]);

  const load = useCallback(async () => {
    const [x, ds] = await Promise.all([companyDetailApi.load(companyId, dealIds), documentApi.list({ companyId })]);
    setD(x); setDocs(ds);
  }, [companyId, dealIds]);
  useEffect(() => { load(); }, [load]);
  // 라운드 · 재무 · 평가를 저장하면 위쪽 숫자(기업가치 · MOIC)도 다시 읽는다 — 패널을 다시 열어야 바뀌던 것(2026-11-02)
  const reloadAll = useCallback(() => { load(); onCompanyChange(); }, [load, onCompanyChange]);

  const gaps = missingProfile(company);
  const lastYear = new Date().getFullYear() - 1;
  // VICS 1316(재무)은 개인투자조합 의무가 아니다(고시 2023-28호 12조③ · 060 문서). 개인투자조합만 투자한 곳은 「실적 없음」을 띄우지 않는다.
  // 창업기획자(고유계정) · 벤처투자조합은 확인 전이라 그대로 띄운다
  const needs1316 = deals.some((x) => {
    const v = vehicles.find((y) => y.id === x.vehicleId);
    return !(v?.kind === 'entrusted' && (v.vehicleType ?? '').includes('개인'));
  });
  const finGap = d && needs1316 ? missingFinancial(d.fin, lastYear) : [];
  // 탭을 바꾸면 내용 높이가 줄어 스크롤이 위로 튄다 — 탭 줄이 보이게 붙잡는다
  const barRef = useRef<HTMLDivElement>(null);
  const pick = (t: Tab) => { setTab(t); requestAnimationFrame(() => barRef.current?.scrollIntoView({ block: 'start' })); };

  return (
    <section data-tour="tabs" className="bg-white border border-slate-200 rounded-lg mt-5 min-h-[70vh]">
      <div ref={barRef} className="flex flex-wrap border-b border-slate-100 px-2 scroll-mt-4">
        {TABS.map((t) => (
          <button key={t} onClick={() => pick(t)}
            className={`px-3 py-2.5 text-sm font-medium border-b-2 -mb-px ${tab === t ? 'border-indigo-600 text-indigo-600' : 'border-transparent text-slate-500 hover:text-slate-800'}`}>
            {t}
            {t === '기업정보' && gaps.length > 0 && <span className="ml-1 text-xs text-amber-700">{gaps.length}</span>}
            {t === '재무' && finGap.length > 0 && <span className="ml-1 text-xs text-amber-700">1</span>}
            {t === '문서 보관함' && docs.length > 0 && <span className="ml-1 text-xs text-slate-400">{docs.length}</span>}
          </button>
        ))}
      </div>

      {!d ? (
        <div className="flex items-center justify-center gap-2 py-10 text-sm text-slate-500"><Loader2 size={14} className="animate-spin" /> 불러오는 중...</div>
      ) : (
        <>
          {tab === '기업정보' && <ProfileTab company={company} d={d} onCompanyChange={onCompanyChange} reload={load} />}
          {tab === '투자정보' && <InvestTab companyId={companyId} deals={deals} vehicles={vehicles} d={d} reload={reloadAll} />}
          {tab === '재무' && <FinanceTab companyId={companyId} rows={d.fin} lastYear={lastYear} needs1316={needs1316} reload={reloadAll} />}
          {tab === '정기보고' && <ReportTab d={d} />}
          {tab === '리스크평가' && <RiskTab d={d} deals={deals} vehicles={vehicles} />}
          {tab === '문서 보관함' && <DocTab companyId={companyId} docs={docs} reload={load} />}
          {tab === '타임라인' && <TimelineTab companyId={companyId} d={d} deals={deals} flows={flows} vehicles={vehicles} reload={load} />}
        </>
      )}
    </section>
  );
};

/* ── 기업정보 ─────────────────────────────────────────────── */

type ProfileKey = 'ceo' | 'bizNo' | 'corpNo' | 'foundedOn' | 'address' | 'industryCode' | 'ventureCert' | 'listed' | 'fiscalMonth' | 'employees'
  | 'parValue' | 'contactName' | 'contactTitle' | 'contactEmail' | 'contactPhone' | 'products' | 'mgmtLevel';
const PROFILE: { k: ProfileKey; label: string; vics?: boolean; type?: 'date' | 'number' | 'yn' | 'mgmt' }[] = [
  { k: 'ceo', label: '대표', vics: true },
  { k: 'bizNo', label: '사업자번호', vics: true },
  { k: 'corpNo', label: '법인번호', vics: true },
  { k: 'foundedOn', label: '설립일', vics: true, type: 'date' },
  { k: 'address', label: '본사 주소', vics: true },
  { k: 'industryCode', label: '업종코드', vics: true },
  { k: 'ventureCert', label: '벤처인증', vics: true, type: 'yn' },
  { k: 'listed', label: '상장', type: 'yn' },
  { k: 'fiscalMonth', label: '결산월', vics: true, type: 'number' },
  { k: 'employees', label: '임직원 수', vics: true, type: 'number' },
  { k: 'parValue', label: '액면가', type: 'number' },
  { k: 'contactName', label: '담당자', vics: true },
  { k: 'contactTitle', label: '담당자 직함' },
  { k: 'contactEmail', label: '담당자 이메일', vics: true },
  { k: 'contactPhone', label: '담당자 전화' },
  { k: 'products', label: '제품 · 서비스' },
  { k: 'mgmtLevel', label: '관리 상태', type: 'mgmt' },
];

const ProfileTab: React.FC<{ company: Company; d: CompanyDetail; onCompanyChange: () => void; reload: () => void }> = ({ company, d, onCompanyChange, reload }) => {
  const [form, setForm] = useState<Record<string, any> | null>(null);
  const [err, setErr] = useState('');
  const show = (p: (typeof PROFILE)[number]) => {
    const v = (company as any)[p.k];
    if (v === null || v === undefined || v === '') return null;
    if (p.type === 'yn') return v ? '있음' : '없음';
    if (p.type === 'date') return dot(v);
    if (p.k === 'fiscalMonth') return `${v}월`;
    if (p.k === 'parValue') return `${Number(v).toLocaleString()}원`;
    if (p.type === 'mgmt') return MGMT_LEVEL.toUi(v);
    return typeof v === 'number' ? v.toLocaleString() : v;
  };
  const save = async () => {
    const out: Record<string, any> = {};
    for (const p of PROFILE) {
      const v = form![p.k];
      out[p.k] = p.type === 'yn' ? (v === '' ? (p.k === 'listed' ? false : null) : v === 'true') : p.type === 'number' ? (v === '' || v == null ? null : Number(v))
        : p.k === 'products' ? (v ?? '') : v === '' ? null : v;
    }
    try { await companyApi.update(company.id, out); setForm(null); setErr(''); onCompanyChange(); } catch (e) { setErr((e as Error).message); }
  };
  const cls = 'w-full max-w-md rounded border border-slate-200 px-1.5 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-200';

  return (
    <>
      <div className="flex justify-end px-4 pt-2">
        {form ? (
          <div className="flex gap-1.5">
            <button onClick={() => setForm(null)} className="px-2.5 py-1 rounded text-xs text-slate-500 hover:bg-slate-100">취소</button>
            <button onClick={save} className="px-2.5 py-1 rounded text-xs font-medium bg-indigo-600 text-white hover:bg-indigo-700">저장</button>
          </div>
        ) : (
          <button onClick={() => setForm(Object.fromEntries(PROFILE.map((p) => {
            const v = (company as any)[p.k];
            return [p.k, p.type === 'yn' ? (v == null ? '' : String(v)) : v ?? ''];
          })))} className="inline-flex items-center gap-1 px-2.5 py-1 rounded text-xs text-slate-600 hover:bg-slate-100"><Pencil size={12} /> 수정</button>
        )}
      </div>
      {err && <p className="px-4 text-xs text-red-600">{err}</p>}
      <table className="w-full"><tbody>
        {PROFILE.map((p) => (
          <tr key={p.k} className="border-b border-slate-50 last:border-b-0">
            <td className="px-4 py-2 text-xs text-slate-500 w-44 whitespace-nowrap">{p.label}{p.vics && <Vics menu="1313" />}</td>
            <td className="px-2 py-1.5 text-sm">
              {form ? (
                p.type === 'yn' ? (
                  <select aria-label={p.label} value={form[p.k]} onChange={(e) => setForm({ ...form, [p.k]: e.target.value })} className={cls}>
                    <option value="">모름</option><option value="true">있음</option><option value="false">없음</option>
                  </select>
                ) : p.type === 'mgmt' ? (
                  <select aria-label={p.label} value={form[p.k]} onChange={(e) => setForm({ ...form, [p.k]: e.target.value })} className={cls}>
                    {opts({ normal: '관리', focus: '집중 관리', closed: '종료' }).map(([k, l]) => <option key={k} value={k}>{l}</option>)}
                  </select>
                ) : (
                  <input aria-label={p.label} type={p.type === 'date' ? 'date' : p.type === 'number' ? 'number' : 'text'} value={form[p.k]}
                    onChange={(e) => setForm({ ...form, [p.k]: e.target.value })} className={cls} />
                )
              ) : (show(p) ?? <span className={`text-xs ${p.vics ? 'text-amber-700' : 'text-slate-300'}`}>{p.vics ? '비어 있음' : '—'}</span>)}
            </td>
          </tr>
        ))}
      </tbody></table>
      <H>기업 연혁</H>
      <EditTable rows={[...d.hist].sort((a, b) => a.occurredOn.localeCompare(b.occurredOn))} onDone={reload} empty="연혁 없음"
        cols={[{ key: 'occurredOn', label: '날짜', type: 'date', required: true, width: '10rem' }, { key: 'text', label: '내용', required: true }]}
        blank={() => ({ occurredOn: todayLocal(), text: '' })} {...crud(history, company.id)} />
    </>
  );
};

/* ── 투자정보 ─────────────────────────────────────────────── */

const InvestTab: React.FC<{ companyId: number; deals: Deal[]; vehicles: Vehicle[]; d: CompanyDetail; reload: () => void }> = ({ companyId, deals, vehicles, d, reload }) => {
  const vName = (id: number | null) => vehicles.find((v) => v.id === id)?.name ?? '—';
  const dealOpts = deals.map((x) => [String(x.id), `${vName(x.vehicleId)} #${x.id}`] as const);
  // 주주명부는 받은 날짜별 스냅샷 — 가장 최근 것을 본다
  const asOfs = Array.from(new Set(d.holders.map((h) => h.asOf))).sort().reverse();
  const [asOf, setAsOf] = useState<string>('');
  const cur = asOf || asOfs[0] || '';
  const holders = d.holders.filter((h) => h.asOf === cur);
  const total = holders.reduce((s, h) => s + Number(h.shares), 0);

  return (
    <div className="pb-3">
      <H>투자 현황</H>
      {deals.length === 0 ? <Empty>건 없음</Empty> : (
        <table className="w-full text-sm"><tbody>
          {deals.map((x) => (
            <tr key={x.id} className="border-b border-slate-50">
              <td className="px-4 py-1.5 w-44 text-xs text-slate-500"><Link to={`/deals/${x.id}`} className="hover:text-indigo-600">{vName(x.vehicleId)}</Link></td>
              <td className="px-2 py-1.5">{x.securityKind ?? '증권 미정'} · {x.investedOn ? dot(x.investedOn) : '검토 중'} · {x.stage}</td>
              <td className="px-4 py-1.5 text-right text-xs text-slate-500 tabular-nums">
                {x.shares != null ? `${x.shares.toLocaleString()}주` : ''}{x.pricePerShare != null ? ` · 주당 ${x.pricePerShare.toLocaleString()}원` : ''}
              </td>
            </tr>
          ))}
        </tbody></table>
      )}

      <H>투자 라운드</H>
      <EditTable rows={[...d.rounds].sort((a, b) => a.closedOn.localeCompare(b.closedOn))} onDone={reload} empty="라운드 기록 없음"
        rowClass={(r) => (r.dealId ? 'bg-indigo-50/30' : '')}
        cols={[
          { key: 'name', label: '라운드', required: true },
          { key: 'closedOn', label: '날짜', type: 'date', required: true },
          { key: 'preMoney', label: 'Pre', type: 'money', right: true },
          { key: 'postMoney', label: 'Post', type: 'money', right: true },
          { key: 'amount', label: '라운드 금액', type: 'money', right: true, required: true },
          { key: 'dealId', label: '우리 참여', type: 'select', options: dealOpts, show: (r) => (r.dealId ? <span className="text-xs text-indigo-600 font-medium">{vName(deals.find((x) => x.id === r.dealId)?.vehicleId ?? null)}</span> : <span className="text-slate-300">—</span>) },
        ]}
        blank={() => ({ name: '', closedOn: todayLocal(), amount: undefined, dealId: null })} {...crud(rounds, companyId)} />

      <H>
        주주명부
        {asOfs.length > 0 && (
          <select aria-label="기준일" value={cur} onChange={(e) => setAsOf(e.target.value)} className="ml-2 rounded border border-slate-200 px-1 py-0.5 text-xs font-normal">
            {asOfs.map((x) => <option key={x} value={x}>{dot(x)} 기준</option>)}
          </select>
        )}
      </H>
      <EditTable rows={holders} onDone={reload} empty="주주명부 없음"
        rowClass={(r) => (r.vehicleId ? 'bg-indigo-50/30' : '')}
        cols={[
          { key: 'asOf', label: '기준일', type: 'date', required: true },
          { key: 'name', label: '주주', required: true },
          { key: 'shareKind', label: '종류', type: 'select', required: true, options: opts({ common: '보통주', preferred: '우선주', cb: 'CB', safe: 'SAFE', option: '스톡옵션' }), show: (r) => SHARE_KIND.toUi(r.shareKind) },
          { key: 'vehicleId', label: '우리 자금원', type: 'select', options: vehicles.map((v) => [String(v.id), v.name] as const), show: (r) => (r.vehicleId ? <span className="text-xs text-indigo-600">{vName(r.vehicleId)}</span> : '') },
          { key: 'shares', label: '주식수', type: 'number', right: true, required: true, show: (r) => (Number(r.shares) ? Number(r.shares).toLocaleString() : <span className="text-slate-400">전환 전</span>) },
        ]}
        tail={{ head: ['지분율'], cells: (r) => [total && Number(r.shares) ? `${((Number(r.shares) / total) * 100).toFixed(2)}%` : '—'] }}
        blank={() => ({ asOf: cur || todayLocal(), name: '', shareKind: 'common', shares: 0, vehicleId: null })} {...crud(shareholders, companyId)} />

      <H>주식변동내역</H>
      <EditTable rows={[...d.changes].sort((a, b) => a.changedOn.localeCompare(b.changedOn))} onDone={reload} empty="기록 없음"
        cols={[{ key: 'changedOn', label: '날짜', type: 'date', required: true, width: '10rem' }, { key: 'kind', label: '종류', required: true, width: '8rem' }, { key: 'detail', label: '내용' }]}
        blank={() => ({ changedOn: todayLocal(), kind: '', detail: '' })} {...crud(shareChanges, companyId)} />

      <H>정부지원사업</H>
      <EditTable rows={d.gov} onDone={reload}
        cols={[{ key: 'name', label: '사업명', required: true }, { key: 'startOn', label: '시작', type: 'date' }, { key: 'endOn', label: '종료', type: 'date' }, { key: 'amount', label: '지원금', type: 'money', right: true }]}
        blank={() => ({ name: '' })} {...crud(govPrograms, companyId)} />

      <H>지식재산권</H>
      <EditTable rows={d.ip} onDone={reload}
        cols={[
          { key: 'kind', label: '종류', type: 'select', required: true, options: opts({ patent: '특허', utility: '실용신안', design: '디자인', trademark: '상표', copyright: '저작권', other: '기타' }), show: (r) => IP_KIND.toUi(r.kind) },
          { key: 'title', label: '명칭', required: true },
          { key: 'regNo', label: '번호', show: (r) => <span className="font-mono text-xs">{r.regNo ?? '—'}</span> },
          { key: 'status', label: '상태', type: 'select', required: true, options: opts({ filed: '출원', registered: '등록', expired: '소멸' }), show: (r) => IP_STATUS.toUi(r.status) },
        ]}
        blank={() => ({ kind: 'patent', title: '', status: 'registered' })} {...crud(ipRights, companyId)} />

      <H>차입금</H>
      <EditTable rows={d.loans} onDone={reload}
        cols={[{ key: 'lender', label: '대출기관', required: true }, { key: 'amount', label: '금액', type: 'money', right: true, required: true }, { key: 'dueOn', label: '만기', type: 'date' }]}
        blank={() => ({ lender: '' })} {...crud(loans, companyId)} />
    </div>
  );
};

/* ── 재무 ─────────────────────────────────────────────────── */

const FinanceTab: React.FC<{ companyId: number; rows: Financial[]; lastYear: number; needs1316: boolean; reload: () => void }> = ({ companyId, rows, lastYear, needs1316, reload }) => {
  const per = (f: Financial) => f.fiscalPeriod ?? 'FY';
  const sorted = [...rows].sort((a, b) => b.fiscalYear - a.fiscalYear || FIN_PERIODS.indexOf(per(a)) - FIN_PERIODS.indexOf(per(b)) || Number(a.isForecast) - Number(b.isForecast));
  // 전년 대비는 같은 기간끼리 — 상반기는 전년 상반기와(#77)
  const actual = (y: number, p: string) => rows.find((x) => x.fiscalYear === y && per(x) === p && !x.isForecast);
  const cols: Col<Financial>[] = [
    { key: 'fiscalYear', label: '연도', type: 'number', required: true, show: (r) => <span className="font-medium">{r.fiscalYear}</span>, width: '6rem' },
    { key: 'fiscalPeriod', label: '기간', type: 'select', required: true, options: FIN_PERIODS.map((p) => [p, FIN_PERIOD_LABEL[p]] as [string, string]),
      show: (r) => (per(r) === 'FY' ? <span className="text-xs text-slate-500">연간</span> : <span className="text-xs font-medium text-sky-700" title={r.periodStart && r.periodEnd ? `${r.periodStart} ~ ${r.periodEnd}` : undefined}>{FIN_PERIOD_LABEL[per(r)]}</span>) },
    { key: 'isForecast', label: '구분', type: 'select', required: true, options: [['false', '실적'], ['true', '전망']], show: (r) => (r.isForecast ? <span className="text-xs text-violet-600">전망</span> : <span className="text-xs text-slate-500">실적</span>) },
    { key: 'revenue', label: '매출', type: 'money', right: true },
    { key: 'operatingIncome', label: '영업이익', type: 'money', right: true },
    { key: 'netIncome', label: '순이익', type: 'money', right: true },
    { key: 'totalAssets', label: '자산', type: 'money', right: true },
    { key: 'totalLiabilities', label: '부채', type: 'money', right: true },
    { key: 'totalEquity', label: '자본', type: 'money', right: true },
    { key: 'source', label: '출처', type: 'select', required: true, options: opts({ manual: '수기', ai: 'AI · 검수 전', ai_checked: 'AI · 검수 완료' }),
      show: (r) => <span className={`text-xs ${r.source === 'ai' ? 'text-amber-700' : 'text-slate-500'}`}>{FIN_SOURCE.toUi(r.source)}</span> },
  ];
  const pctCell = (v: number | null, digits = 0, good = true) => (v === null ? <span className="text-slate-300">—</span>
    : <span className={good ? (v >= 0 ? 'text-emerald-700' : 'text-red-600') : 'text-slate-600'}>{(v * 100).toFixed(digits)}%</span>);

  return (
    <div className="pb-1">
      <div className="px-4 pt-3 text-xs text-slate-500">
        단위 원<Vics menu="1316" />
        {needs1316 && missingFinancial(rows, lastYear).length > 0 && <span className="ml-2 text-amber-700">{lastYear} 실적 없음</span>}
        {!needs1316 && <span className="ml-2 text-slate-400">개인투자조합만 투자 — 1316 보고 의무 없음</span>}
      </div>
      <EditTable rows={sorted} cols={cols} onDone={reload} empty="재무 없음"
        blank={() => ({ fiscalYear: lastYear, fiscalPeriod: 'FY', isForecast: false, source: 'manual' } as Partial<Financial>)}
        onSave={(id, v) => {
          const b = { ...v, isForecast: String(v.isForecast) === 'true' } as Partial<Financial>;
          return id ? financials.update(id, b) : financials.create({ ...b, companyId });
        }}
        onRemove={(id) => financials.remove(id)}
        tail={{
          head: ['매출성장', '영업이익률', '부채비율'],
          cells: (f) => {
            const prev = actual(f.fiscalYear - 1, per(f)) ?? null;
            const g = prev && prev.revenue && f.revenue !== null ? (f.revenue - prev.revenue) / prev.revenue : null;
            const om = f.revenue ? (f.operatingIncome ?? 0) / f.revenue : null;
            const dr = f.totalEquity && f.totalEquity > 0 && f.totalLiabilities !== null ? f.totalLiabilities / f.totalEquity : null;
            return [pctCell(g), pctCell(om, 1), pctCell(dr, 0, false)];
          },
        }} />
    </div>
  );
};

/* ── 정기보고 · 리스크평가 — 읽기만 ───────────────────────────── */

const ReportTab: React.FC<{ d: CompanyDetail }> = ({ d }) => {
  const title = (id: number) => d.reportRounds.find((r) => r.id === id)?.title ?? `회차 ${id}`;
  if (d.reqs.length === 0) return <Empty>요청한 정기보고 없음</Empty>;
  return (
    <table className="w-full text-sm">
      <thead><tr className="text-xs text-slate-400 border-b border-slate-100"><th className="text-left font-medium px-4 py-2">회차</th><th className="text-left font-medium px-2">요청</th><th className="text-left font-medium px-2">제출</th><th className="text-left font-medium px-2">확정</th><th className="text-left font-medium px-4">상태</th></tr></thead>
      <tbody>{[...d.reqs].sort((a, b) => b.roundId - a.roundId).map((r) => (
        <tr key={r.id} className="border-b border-slate-50">
          <td className="px-4 py-2"><Link to="/reports/periodic" className="hover:text-indigo-600">{title(r.roundId)}</Link></td>
          <td className="px-2 text-xs tabular-nums text-slate-500">{dot(r.sentAt)}</td>
          <td className="px-2 text-xs tabular-nums text-slate-500">{dot(r.submittedAt)}</td>
          <td className="px-2 text-xs tabular-nums text-slate-500">{dot(r.confirmedAt)}</td>
          <td className="px-4"><span className={`rounded-full px-2 py-0.5 text-xs font-medium ${REQ_STYLE[r.status] ?? ''}`}>{REQ_STATUS.toUi(r.status) ?? r.status}</span></td>
        </tr>))}</tbody>
    </table>
  );
};

const RiskTab: React.FC<{ d: CompanyDetail; deals: Deal[]; vehicles: Vehicle[] }> = ({ d, deals, vehicles }) => {
  const code = (id: number) => d.riskRounds.find((r) => r.id === id)?.code ?? '';
  const list = [...d.risks].sort((a, b) => code(b.roundId).localeCompare(code(a.roundId)) || a.dealId - b.dealId);
  const roundIds = Array.from(new Set(list.map((r) => r.roundId))).sort((a, b) => code(a).localeCompare(code(b)));
  const vName = (dealId: number) => vehicles.find((v) => v.id === deals.find((x) => x.id === dealId)?.vehicleId)?.name ?? `#${dealId}`;
  const badge = (g: string | null) => (g ? <span className={`rounded-full px-2 py-0.5 text-xs font-semibold ${GRADE_STYLE[g] ?? ''}`}>{RISK_GRADE.toUi(g) ?? g}</span> : <span className="text-slate-300">—</span>);
  if (list.length === 0) return <Empty>평가 없음</Empty>;
  return (
    <>
      <div className="px-4 pt-3 flex flex-wrap items-center gap-3 text-xs">
        <span className="text-slate-500">분기별 승인 등급</span>
        {roundIds.map((rid) => {
          const g = list.find((a) => a.roundId === rid && a.status === 'approved')?.grade ?? null;
          return <span key={rid} className="inline-flex items-center gap-1"><span className="text-slate-400">{code(rid).slice(2).replace('-Q', ' Q')}</span>{badge(g)}</span>;
        })}
        <Link to="/reports/risk" className="ml-auto text-indigo-600 hover:underline">리스크 평가로</Link>
      </div>
      <table className="w-full text-sm mt-2">
        <tbody>{list.map((r) => (
          <tr key={r.id} className="border-b border-slate-50 last:border-b-0">
            <td className="px-4 py-2 text-xs text-slate-500 tabular-nums w-20">{code(r.roundId)}</td>
            <td className="px-2 py-2 text-xs text-slate-600">{vName(r.dealId)}</td>
            <td className="px-2 py-2">{badge(r.grade)}</td>
            <td className="px-2 py-2 text-xs text-slate-500">{RISK_STATUS.toUi(r.status) ?? r.status}</td>
            <td className="px-2 py-2 text-sm text-slate-700">{r.opinion}</td>
            <td className="px-4 py-2 text-xs text-slate-400 tabular-nums text-right">{dot(r.approvedAt ?? r.submittedAt)}</td>
          </tr>
        ))}</tbody>
      </table>
    </>
  );
};

/* ── 문서 보관함 ──────────────────────────────────────────── */

const DocTab: React.FC<{ companyId: number; docs: Doc[]; reload: () => void }> = ({ companyId, docs, reload }) => {
  const ref = useRef<HTMLInputElement>(null);
  const [msg, setMsg] = useState('');
  const [busy, setBusy] = useState(false);
  const upload = async (files: FileList | null) => {
    if (!files?.length) return;
    setBusy(true); setMsg('');
    const errs: string[] = [];
    for (const f of Array.from(files)) {
      try { await documentApi.upload(f, { companyId, kind: guessKind(f.name) }); } catch (e) { errs.push(`${f.name}: ${(e as Error).message}`); }
    }
    setBusy(false); setMsg(errs.join(' · ')); reload();
    if (ref.current) ref.current.value = '';
  };
  return (
    <>
      <div className="flex items-center justify-end gap-2 px-4 pt-2">
        {msg && <span className="text-xs text-red-600 mr-auto">{msg}</span>}
        <label className="inline-flex items-center gap-1 px-2.5 py-1 rounded text-xs font-medium bg-indigo-50 text-indigo-700 hover:bg-indigo-100 cursor-pointer">
          {busy ? <Loader2 size={12} className="animate-spin" /> : <Upload size={12} />} 올리기
          <input ref={ref} type="file" multiple accept={ACCEPT} className="hidden" onChange={(e) => upload(e.target.files)} />
        </label>
      </div>
      {docs.length === 0 ? <Empty>문서 없음</Empty> : (
        <ul className="divide-y divide-slate-50">
          {docs.map((x) => (
            <li key={x.id} className="flex items-center gap-3 px-4 py-2.5 text-sm">
              <FileText size={15} className="text-slate-400" />
              <Link to={`/documents?id=${x.id}`} className="flex-1 truncate hover:text-indigo-600">{x.fileName}</Link>
              <span className="text-xs text-slate-400">{DOC_KIND.toUi(x.kind)} · {dot(x.createdAt)}</span>
              <span className={`text-xs ${DOC_STATUS_STYLE[x.extractStatus]}`}>{DOC_STATUS.toUi(x.extractStatus)}</span>
              <button aria-label="내려받기" onClick={() => documentApi.download(x.id, x.fileName).catch((e) => window.dispatchEvent(new CustomEvent('app:error', { detail: (e as Error).message })))} className="p-1 rounded text-slate-400 hover:text-slate-700 hover:bg-slate-100"><Download size={13} /></button>
            </li>
          ))}
        </ul>
      )}
    </>
  );
};

/* ── 타임라인 ─────────────────────────────────────────────── */

const TimelineTab: React.FC<{ companyId: number; d: CompanyDetail; deals: Deal[]; flows: Cashflow[]; vehicles: Vehicle[]; reload: () => void }> = ({ companyId, d, deals, flows, vehicles, reload }) => {
  const vName = (id: number) => vehicles.find((v) => v.id === id)?.name ?? '';
  const comments = d.notes.filter((n) => n.kind === 'comment').sort((a, b) => b.occurredOn.localeCompare(a.occurredOn));
  // 투자 · 회수는 원장에서 — 같은 날 같은 종류는 한 줄로
  const ledger = flows.filter((f) => f.dealId !== null && deals.some((x) => x.id === f.dealId) && (f.kind === 'investment' || f.kind === 'exit' || f.kind === 'dividend'))
    .map((f) => ({ id: -f.id, kind: f.kind === 'investment' ? '투자' : f.kind === 'exit' ? '회수' : '배당', occurredOn: f.occurredOn, who: vName(f.vehicleId), body: `${money(f.amount)}${f.memo ? ` · ${f.memo}` : ''}` }));
  const items = [
    ...d.notes.filter((n) => n.kind !== 'comment').map((n) => ({ id: n.id, kind: NOTE_KIND.toUi(n.kind) ?? n.kind, occurredOn: n.occurredOn, who: n.authorName, body: n.body })),
    ...ledger,
  ].sort((a, b) => b.occurredOn.localeCompare(a.occurredOn));
  const [armed, setArmed] = useState<number | null>(null);

  const cols: Col<any>[] = [
    { key: 'kind', label: '종류', type: 'select', required: true, options: opts({ memo: '메모', meeting: '미팅', review: '심사', document: '자료', comment: '관리 코멘트' }) },
    { key: 'occurredOn', label: '날짜', type: 'date', required: true },
    { key: 'authorName', label: '쓴 사람', required: true },
    { key: 'body', label: '내용', required: true },
  ];
  const remove = async (id: number) => {
    if (armed !== id) { setArmed(id); return; }
    await notes.remove(id); setArmed(null); reload();
  };

  return (
    <>
      {comments.length > 0 && (
        <div className="mx-4 mt-3 rounded-lg bg-amber-50/60 border border-amber-100 px-3 py-2">
          <div className="text-xs font-semibold text-amber-800 mb-1">관리 코멘트</div>
          {comments.map((c) => (
            <p key={c.id} className="group text-sm text-slate-800">{c.body} <span className="text-xs text-slate-400">· {c.authorName} {dot(c.occurredOn)}</span>
              <button onClick={() => remove(c.id)} className={`ml-1 text-xs ${armed === c.id ? 'text-red-600' : 'text-slate-300 opacity-0 group-hover:opacity-100 hover:text-red-600'}`}>지우기</button></p>
          ))}
        </div>
      )}
      {items.length === 0 ? <Empty>기록 없음</Empty> : (
        <ol className="px-4 py-3 space-y-3">
          {items.map((t) => (
            <li key={t.id} className="group flex gap-3">
              <span className="w-20 flex-shrink-0 text-xs text-slate-400 tabular-nums pt-0.5">{dot(t.occurredOn)}</span>
              <span className={`rounded-full px-2 py-0.5 text-xs font-medium h-fit ${t.id < 0 ? 'bg-indigo-50 text-indigo-600' : 'bg-slate-100 text-slate-600'}`}>{t.kind}</span>
              <span className="text-sm text-slate-800 flex-1">{t.body} <span className="text-xs text-slate-400">· {t.who}</span></span>
              {t.id > 0 && <button onClick={() => remove(t.id)} className={`text-xs ${armed === t.id ? 'text-red-600' : 'text-slate-300 opacity-0 group-hover:opacity-100 hover:text-red-600'}`}>지우기</button>}
            </li>
          ))}
        </ol>
      )}
      {/* 쓰기만 — 목록은 위에서 본다 */}
      <EditTable rows={[]} cols={cols} onDone={reload} empty="" addLabel="기록 추가"
        blank={() => ({ kind: 'memo', occurredOn: todayLocal(), authorName: '', body: '' })} {...crud(notes, companyId)} />
    </>
  );
};
